// BUILD-042 (Oct 24) - Employee details: the fields, and the ONE save used by BOTH guided setup (step 1) and the later "Edit" on the HR management overview.
//
// Saving goes through the EXISTING guarded routes and nothing new:
//   * job fields (title, department, manager) -> POST /hr/admin/employees/:id/employment-changes (createEmploymentChange). ONLY the fields that actually
//     changed are sent (null only for a deliberate clear), so a Save with nothing changed writes nothing, an untouched field is never overwritten, and a
//     stale snapshot is never resent (setupModel.jobChangeBody). Classification is NOT edited here: no accepted value set exists for it (read-only display).
//   * personal phone -> POST /hr/admin/employees/:id/personal (upsertHrPersonalDetail), the ONLY phone writer; offered only when the caller may use it.
// The server's no-self-finalization rule is explained in plain words, never shown as a raw code. No mandatory-field policy is added here.
import { useCallback, useEffect, useState } from 'react';
import {
  createEmploymentChange, listHrAdminEmployees, listHrDepartments, upsertHrPersonalDetail,
  type HrAdminEmployeeListEntry, type HrDepartment,
} from '../../../api/client';
import { formatLocalDate } from '../hrFormat';
import { formatEdited, phoneForSave, phoneProblem } from '../phone';
import { changedJobFields, jobChangeBody, type JobField } from './setupModel';
import { NOT_AVAILABLE, resolveIdentity } from './detailsModel';
import type { PersonInfo } from './setupTypes';

/** The existing no-self-finalization rule, in plain words (same wording as the Employment section). */
export const OWN_RECORD_MESSAGE = 'You can’t record changes to your own employment record, including your department. This is a built-in safeguard that applies to everyone. Another HR administrator can record the change for you (you can give someone the “HR management” permission under Access). If something on your record is wrong, use “Request a correction” in My HR.';

export interface DetailsValues { title: string; departmentId: string; managerEmployeeId: string; phone: string }
export interface DetailsBaseline {
  hireDate: string | null; title: string | null; departmentId: string | null; managerEmployeeId: string | null; classification: string | null;
  phone: string | null; phoneAvailable: boolean;
}
export function valuesFromBaseline(b: DetailsBaseline): DetailsValues {
  return { title: b.title ?? '', departmentId: b.departmentId ?? '', managerEmployeeId: b.managerEmployeeId ?? '', phone: b.phone ?? '' };
}
const norm = (s: string | null | undefined) => (s ?? '').trim();
export function detailsChanged(b: DetailsBaseline, v: DetailsValues): { job: JobField[]; phone: boolean } {
  // An INVALID phone entry still counts as an edit (the screen must warn before leaving); it is refused at save time, never written.
  const p = phoneForSave(v.phone, b.phone);
  return { job: changedJobFields(b, v), phone: b.phoneAvailable && (!p.ok || p.changed) };
}
/** The inline message for an edited phone that cannot be saved (null when fine, unedited, or the field is not editable here). */
export function detailsPhoneProblem(b: DetailsBaseline, v: DetailsValues): string | null {
  return b.phoneAvailable ? phoneProblem(v.phone, b.phone) : null;
}

export type DetailsSaveOutcome = { ok: true; wrote: boolean } | { ok: false; message: string; saved?: string; notSaved?: string };

export async function saveEmployeeDetails(args: {
  employeeId: string; effectiveDate: string; baseline: DetailsBaseline; values: DetailsValues; reason?: string;
}): Promise<DetailsSaveOutcome> {
  const { employeeId, effectiveDate, baseline, values } = args;
  if (!effectiveDate) return { ok: false, message: 'The effective date is missing, so nothing was saved. Your entries are still here.', notSaved: 'all details' };
  if (values.managerEmployeeId && values.managerEmployeeId === employeeId) {
    return { ok: false, message: 'A person can’t be their own manager. Choose someone else, or leave the manager unassigned. Nothing was saved and your entries are still here.', notSaved: 'all details' };
  }
  // An edited phone that is not acceptable blocks the WHOLE save before any request (nothing is half-written, every entry stays on screen).
  const phoneIssue = detailsPhoneProblem(baseline, values);
  if (phoneIssue) return { ok: false, message: `${phoneIssue} Nothing was saved and your entries are still here.`, notSaved: 'all details' };
  const changed = detailsChanged(baseline, values);
  if (changed.job.length === 0 && !changed.phone) return { ok: true, wrote: false };

  let savedJob = false;
  // ONLY the changed fields are sent (an absent key keeps what is on record; the server carries it forward from the term in effect on the effective date).
  const body = jobChangeBody(effectiveDate, baseline, values, args.reason);
  if (body) {
    try {
      await createEmploymentChange(employeeId, body as Parameters<typeof createEmploymentChange>[1]);
      savedJob = true;
    } catch (e: any) {
      const own = e?.status === 403 && /no-self-finalization/.test(String(e?.message || ''));
      return {
        ok: false,
        message: `${own ? OWN_RECORD_MESSAGE : (e?.message || 'The job details could not be saved.')} Nothing was saved and your entries are still here.`,
        notSaved: changed.phone ? 'job details and phone' : 'job details',
      };
    }
  }
  if (changed.phone) {
    try {
      const p = phoneForSave(values.phone, baseline.phone);
      await upsertHrPersonalDetail(employeeId, { personalPhone: p.ok ? p.value : null });
    } catch (e: any) {
      return {
        ok: false,
        message: `${savedJob ? 'The job details were saved, but the' : 'The'} personal phone was not saved: ${e?.message || 'the request failed'}. Your entries are still here.`,
        saved: savedJob ? 'job details' : undefined, notSaved: 'personal phone',
      };
    }
  }
  return { ok: true, wrote: true };
}

// ─── Options (active departments, real people with HR records) ──────────────────────────────────────────────────────────────────
export interface DirectoryOptions {
  departments: HrDepartment[] | null;
  people: HrAdminEmployeeListEntry[] | null;
  error: string | null;
  retry: () => void;
}
export function useDirectoryOptions(): DirectoryOptions {
  const [departments, setDepartments] = useState<HrDepartment[] | null>(null);
  const [people, setPeople] = useState<HrAdminEmployeeListEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    listHrDepartments({ includeDisabled: true }).then((d) => setDepartments(d.departments)).catch(() => setError('Departments could not be loaded right now.'));
    listHrAdminEmployees().then((d) => setPeople(d.employees)).catch(() => setError((prev) => prev ?? 'People could not be loaded right now.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { departments, people, error, retry: load };
}

const inputCls = 'border border-gray-300 rounded-md px-3 py-2 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-[#4AABDE]/40 disabled:bg-gray-100 disabled:text-gray-500';
const readOnlyCls = 'border border-gray-200 rounded-md px-3 py-2 text-sm w-full bg-gray-100 text-gray-700';

function Field({ label, htmlFor, children, hint, required }: { label: string; htmlFor: string; children: React.ReactNode; hint?: string; required?: boolean }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label htmlFor={htmlFor} className="text-xs font-medium text-gray-700">{label}{required && <span className="text-red-600"> *</span>}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

export const PERSONAL_PHONE_LABEL = 'Personal phone';
export const PERSONAL_PHONE_HINT = 'Saved as the personal phone in Personal & Contacts. It is for HR records only and is not shown in the company directory.';
export const PERSONAL_PHONE_UNAVAILABLE = 'Not editable here: saving a personal phone needs permission to edit this person’s personal details, which you don’t have. The job details above can still be saved.';

export function EmployeeDetailsFields({ idPrefix, employeeId, person, baseline, values, onChange, options, disabled, hireDateNote, extra }: {
  idPrefix: string;
  /** The person being edited: they are never offered as their own manager. */
  employeeId: string;
  person: PersonInfo;
  baseline: DetailsBaseline;
  values: DetailsValues;
  onChange: (next: DetailsValues) => void;
  options: DirectoryOptions;
  disabled?: boolean;
  hireDateNote?: string;
  /** Extra full-width content (the overview's edit form adds its effective date here). */
  extra?: React.ReactNode;
}) {
  const set = (patch: Partial<DetailsValues>) => onChange({ ...values, ...patch });
  const { departments, people } = options;
  const activeDepartments = (departments ?? []).filter((d) => !d.disabled);
  const heldDepartment = baseline.departmentId ? (departments ?? []).find((d) => d.departmentId === baseline.departmentId) : null;
  const heldDisabled = !!heldDepartment?.disabled;
  const managers = (people ?? []).filter((p) => p.hasHrRecord && !p.archived);
  const managerKnown = !values.managerEmployeeId || managers.some((p) => p.employeeId === values.managerEmployeeId);
  const heldManager = values.managerEmployeeId ? (people ?? []).find((p) => p.employeeId === values.managerEmployeeId) : null;
  // Name and work email come from the authorized directory entry for this person when the roster has loaded; otherwise "Not available" (never a guess).
  const identity = resolveIdentity(person, (people ?? []).find((p) => p.employeeId === employeeId));
  // The phone message appears once the person leaves the field (or pasted something unusable); the save is blocked by the same rule either way.
  const [phoneLeft, setPhoneLeft] = useState(false);
  const phoneIssue = baseline.phoneAvailable ? phoneProblem(values.phone, baseline.phone) : null;
  const phoneMsgId = `${idPrefix}-phone-msg`;
  const showPhoneIssue = !!phoneIssue && phoneLeft;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4" data-testid="employee-details-fields">
      <Field label="Name" htmlFor={`${idPrefix}-name`} hint={undefined}>
        <input id={`${idPrefix}-name`} className={readOnlyCls} value={identity.name ?? NOT_AVAILABLE} readOnly aria-readonly="true" data-testid="details-name" />
      </Field>
      <Field label="Work email" htmlFor={`${idPrefix}-email`}>
        <input id={`${idPrefix}-email`} className={readOnlyCls} value={identity.email ?? NOT_AVAILABLE} readOnly aria-readonly="true" data-testid="details-email" />
      </Field>
      <Field label="Hire date" htmlFor={`${idPrefix}-hire`} required hint={hireDateNote}>
        <input id={`${idPrefix}-hire`} className={readOnlyCls} value={baseline.hireDate ? formatLocalDate(baseline.hireDate) : 'Not recorded'} readOnly aria-readonly="true" data-testid="details-hire-date" />
      </Field>
      <Field label="Job title" htmlFor={`${idPrefix}-title`}>
        <input id={`${idPrefix}-title`} className={inputCls} value={values.title} disabled={disabled} onChange={(e) => set({ title: e.target.value })} placeholder="Job title" data-testid="details-title" />
      </Field>
      <Field label="Department" htmlFor={`${idPrefix}-dept`} hint={options.error && !departments ? options.error : undefined}>
        <select id={`${idPrefix}-dept`} className={inputCls} value={values.departmentId} disabled={disabled || !departments} onChange={(e) => set({ departmentId: e.target.value })} data-testid="details-department">
          <option value="">{departments ? 'Not assigned' : 'Loading departments…'}</option>
          {heldDisabled && heldDepartment && <option value={heldDepartment.departmentId}>{heldDepartment.name} (disabled)</option>}
          {activeDepartments.map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
        </select>
      </Field>
      <Field label="Manager" htmlFor={`${idPrefix}-manager`} hint={options.error && !people ? options.error : undefined}>
        <select id={`${idPrefix}-manager`} className={inputCls} value={values.managerEmployeeId} disabled={disabled || !people} onChange={(e) => set({ managerEmployeeId: e.target.value })} data-testid="details-manager">
          <option value="">{people ? 'Not assigned' : 'Loading people…'}</option>
          {!managerKnown && <option value={values.managerEmployeeId}>{heldManager?.name ?? 'Current manager'}</option>}
          {managers.filter((p) => p.employeeId !== employeeId && p.name).map((p) => <option key={p.employeeId} value={p.employeeId}>{p.name}</option>)}
        </select>
      </Field>
      {/* Employment classification: no accepted value set exists and no screen may invent one, so it is not an input. An existing value is shown read-only. */}
      {baseline.classification && (
        <Field label="Employment classification" htmlFor={`${idPrefix}-class`} hint="Shown for reference. It is not changed on this screen.">
          <input id={`${idPrefix}-class`} className={readOnlyCls} value={baseline.classification} readOnly aria-readonly="true" data-testid="details-classification-readonly" />
        </Field>
      )}
      <Field label={PERSONAL_PHONE_LABEL} htmlFor={`${idPrefix}-phone`} hint={baseline.phoneAvailable ? PERSONAL_PHONE_HINT : PERSONAL_PHONE_UNAVAILABLE}>
        <input
          id={`${idPrefix}-phone`} type="tel" inputMode="tel" autoComplete="off"
          className={`${inputCls}${showPhoneIssue ? ' border-red-400' : ''}`}
          value={baseline.phoneAvailable ? values.phone : ''} disabled={disabled || !baseline.phoneAvailable}
          // Paste-friendly: a pasted number is formatted right away; typing is left alone until the person leaves the field. A stored value that was not edited is never reformatted.
          onChange={(e) => {
            const raw = e.target.value;
            const pasted = (e.nativeEvent as InputEvent).inputType === 'insertFromPaste';
            if (pasted) setPhoneLeft(true);
            set({ phone: pasted ? formatEdited(raw, baseline.phone) : raw });
          }}
          onBlur={() => { setPhoneLeft(true); const f = formatEdited(values.phone, baseline.phone); if (f !== values.phone) set({ phone: f }); }}
          placeholder={baseline.phoneAvailable ? 'Personal phone number' : 'Not available'} aria-label={PERSONAL_PHONE_LABEL}
          aria-invalid={showPhoneIssue ? true : undefined} aria-describedby={showPhoneIssue ? phoneMsgId : undefined} data-testid="details-phone"
        />
        {showPhoneIssue && <p id={phoneMsgId} role="alert" className="text-[11px] text-red-700" data-testid="details-phone-message">{phoneIssue}</p>}
      </Field>
      {extra}
    </div>
  );
}
